import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  Container,
  Header,
  Select,
  SelectProps,
  Table,
  Textarea,
} from '@amzn/awsui-components-react';
import { ArenaDefinition, ArenaRequirement, RequirementCriticality } from '@amzn/aws-jam-typescript-client';
import { cloneDeep, isEqual } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { Nullable } from '@/src/types/common';
import { SaveArenaDefinition } from '@/src/utils/arena.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import LeavePageGuard from '@/src/components/common/LeavePageGuard/LeavePageGuard';
import ArenaEditActions from './ArenaEditActions';
import styles from './Arena.module.scss';

interface ArenaRequirementsProps {
  arenaDefinition: Nullable<ArenaDefinition>;
  canEdit: boolean;
  onSave: SaveArenaDefinition;
}

const ArenaRequirements: React.FC<ArenaRequirementsProps> = ({ arenaDefinition, canEdit, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<ArenaRequirement[]>([]);

  const savedRequirements = arenaDefinition?.requirements ?? [];
  const requirements = editing ? draft : savedRequirements;
  const dimensions = arenaDefinition?.dimensions ?? [];

  const criticalityOptions: SelectProps.Option[] = [
    {
      value: RequirementCriticality.HARD,
      label: t(i18nKeys.challenges.challengeDetails.arena.requirements.hard),
      description: t(i18nKeys.challenges.challengeDetails.arena.requirements.hardDescription),
    },
    {
      value: RequirementCriticality.STANDARD,
      label: t(i18nKeys.challenges.challengeDetails.arena.requirements.standard),
      description: t(i18nKeys.challenges.challengeDetails.arena.requirements.standardDescription),
    },
  ];

  const dimensionOptions: SelectProps.Option[] = [];

  for (const dimension of dimensions) {
    dimensionOptions.push({
      value: dimension.key,
      label: dimension.name || t(i18nKeys.challenges.challengeDetails.arena.dimensions.untitled),
    });
  }

  const getDimensionName = (dimensionKey: string | undefined) => {
    for (const dimension of dimensions) {
      if (dimension.key === dimensionKey) {
        return dimension.name;
      }
    }

    return undefined;
  };

  const getCriticalityOption = (criticality: RequirementCriticality | undefined) => {
    for (const option of criticalityOptions) {
      if (option.value === criticality) {
        return option;
      }
    }

    return null;
  };

  const getDimensionOption = (dimensionKey: string | undefined) => {
    for (const option of dimensionOptions) {
      if (option.value === dimensionKey) {
        return option;
      }
    }

    return null;
  };

  const handleEdit = () => {
    setDraft(cloneDeep(savedRequirements));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await onSave({ requirements: draft });
    setSaving(false);

    if (saved) {
      setEditing(false);
    }
  };

  const updateRequirement = (index: number, changes: Partial<ArenaRequirement>) => {
    const updated = [...requirements];
    updated[index] = { ...updated[index], ...changes };
    setDraft(updated);
  };

  const addRequirement = () => {
    setDraft([...requirements, { id: v4(), statement: '', criticality: RequirementCriticality.STANDARD }]);
  };

  const removeRequirement = (index: number) => {
    const updated = [...requirements];
    updated.splice(index, 1);
    setDraft(updated);
  };

  const renderCriticality = (criticality: RequirementCriticality | undefined) => {
    if (criticality === RequirementCriticality.HARD) {
      return <Badge color="red">{t(i18nKeys.challenges.challengeDetails.arena.requirements.hard)}</Badge>;
    }

    if (criticality === RequirementCriticality.STANDARD) {
      return <Badge color="blue">{t(i18nKeys.challenges.challengeDetails.arena.requirements.standard)}</Badge>;
    }

    return '-';
  };

  const renderContent = () => {
    if (!editing) {
      return (
        <Table
          variant="embedded"
          items={requirements}
          trackBy="id"
          wrapLines
          columnDefinitions={[
            {
              id: 'statement',
              header: t(i18nKeys.challenges.challengeDetails.arena.requirements.statement),
              minWidth: 350,
              cell: (item) => item.statement || '-',
            },
            {
              id: 'criticality',
              header: t(i18nKeys.challenges.challengeDetails.arena.requirements.criticality),
              cell: (item) => renderCriticality(item.criticality),
            },
            {
              id: 'dimension',
              header: t(i18nKeys.challenges.challengeDetails.arena.requirements.dimension),
              cell: (item) => getDimensionName(item.dimensionKey) || '-',
            },
          ]}
          empty={<Box>{t(i18nKeys.challenges.challengeDetails.arena.requirements.emptyState)}</Box>}
          data-testid="arena-requirements-table"
        />
      );
    }

    return (
      <Table
        variant="embedded"
        items={requirements}
        trackBy="id"
        wrapLines
        columnDefinitions={[
          {
            id: 'statement',
            header: t(i18nKeys.challenges.challengeDetails.arena.requirements.statement),
            minWidth: 350,
            cell: (item) => (
              <Textarea
                className={styles.autoGrow}
                value={item.statement ?? ''}
                rows={2}
                onChange={({ detail }) => updateRequirement(requirements.indexOf(item), { statement: detail.value })}
              />
            ),
          },
          {
            id: 'criticality',
            header: t(i18nKeys.challenges.challengeDetails.arena.requirements.criticality),
            width: 200,
            cell: (item) => (
              <Select
                selectedOption={getCriticalityOption(item.criticality)}
                options={criticalityOptions}
                expandToViewport
                onChange={({ detail }) =>
                  updateRequirement(requirements.indexOf(item), {
                    criticality: detail.selectedOption.value as RequirementCriticality,
                  })
                }
              />
            ),
          },
          {
            id: 'dimension',
            header: t(i18nKeys.challenges.challengeDetails.arena.requirements.dimension),
            width: 260,
            cell: (item) => (
              <Select
                selectedOption={getDimensionOption(item.dimensionKey)}
                options={dimensionOptions}
                expandToViewport
                placeholder={t(i18nKeys.challenges.challengeDetails.arena.requirements.selectDimension)}
                onChange={({ detail }) =>
                  updateRequirement(requirements.indexOf(item), { dimensionKey: detail.selectedOption.value })
                }
              />
            ),
          },
          {
            id: 'actions',
            header: '',
            width: 70,
            cell: (item) => (
              <ButtonDropdown
                variant="icon"
                expandToViewport
                ariaLabel={t(i18nKeys.events.eventDetails.buttons.actionsPlaceholder)}
                items={[{ id: 'remove', text: t(i18nKeys.challenges.challengeDetails.arena.remove) || '' }]}
                onItemClick={({ detail }) => {
                  if (detail.id === 'remove') {
                    removeRequirement(requirements.indexOf(item));
                  }
                }}
              />
            ),
          },
        ]}
        empty={<Box>{t(i18nKeys.challenges.challengeDetails.arena.requirements.emptyState)}</Box>}
        footer={
          <Button iconName="add-plus" onClick={addRequirement} data-testid="arena-requirements-add">
            {t(i18nKeys.challenges.challengeDetails.arena.requirements.add)}
          </Button>
        }
        data-testid="arena-requirements-editor"
      />
    );
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          counter={`(${requirements.length})`}
          description={t(i18nKeys.challenges.challengeDetails.arena.requirements.description)}
          actions={
            <ArenaEditActions
              canEdit={canEdit}
              editing={editing}
              saving={saving}
              onEdit={handleEdit}
              onCancel={() => setEditing(false)}
              onSave={() => void handleSave()}
            />
          }
        >
          {t(i18nKeys.challenges.challengeDetails.arena.requirements.title)}
        </Header>
      }
    >
      <LeavePageGuard when={editing && !isEqual(draft, savedRequirements)} />
      {renderContent()}
    </Container>
  );
};

export default ArenaRequirements;
